import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import NumberInputWithControls from "@/components/NumberInputWithControls";
import { EntryFinishPopup } from "@/components/PredictionMarket/EntryFinishPopup";
import { EntrySharePopup } from "@/components/PredictionMarket/EntrySharePopup";
import { ChoiceCard } from "@/components/PredictionMarket/MarketCardList/MultipleChoices/ChoiceCard";
import { Title } from "@/components/PredictionMarket/MarketCardList/Title";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "@/components/ui/select";
import { usePredictionMarketEntry } from "@/hooks/usePredictionMarketEntry";
import { InsufficientModal } from "@/pages/Rounds/SelectCaptain/components/InsufficientModal";
import { SwitchIcon, ThumbaCoin } from "@roman/shared/components/Icons";
import { BACKGROUND_MULTIPLE_COLORS, MULTIPLE_COLORS } from "@roman/shared/constants/predictionChart";
import { useGenerateOgpImage } from "@roman/shared/hooks/PredictionMarket/useGenerateOgpImage";
import type { PredictionMarketHistoryResponse } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketHistory";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type {
  PredictionMarketChoice,
  PredictionMarketChoiceWithProbability,
} from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import {
  PredictionMarketType,
  getPredictionMarketType,
  getYesNoChoices,
} from "@roman/shared/utils/predictionMarket/utils";
import { ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

enum ModalStatus {
  EntryCreation = "EntryCreation",
  Finish = "Finish",
  Share = "Share",
}

interface DetailPageEntryPopupProps {
  predictionMarket: PredictionMarketHistoryResponse["predictionMarket"];
  selectedChoice: PredictionMarketChoice;
  isShowQuickAdd?: boolean;
  isNeedConfirmation?: boolean;
  handleConfirmEntry?: (points: number, newChoice: PredictionMarketChoiceWithProbability) => void;
  isChangeableMultipleChoice?: boolean;
}

export const DetailPageEntryPopup: React.FC<DetailPageEntryPopupProps> = ({
  predictionMarket,
  selectedChoice,
  isShowQuickAdd = true,
  isNeedConfirmation = false,
  handleConfirmEntry,
  isChangeableMultipleChoice = true,
}) => {
  const navigate = useNavigate();
  const fetchUser = useAuthStore((state) => state.fetchAndSetUser);
  const [localSelectedChoice, setLocalSelectedChoice] = useState<PredictionMarketChoice>(selectedChoice);
  const [modalStatus, setModalStatus] = useState<ModalStatus>(ModalStatus.EntryCreation);
  const [openInsufficientModal, setOpenInsufficientModal] = useState(false);
  const user = useAuthStore((state) => state.user);
  const userPoints = user ? Number.parseInt(user.freePoints) : 0;
  const [displayedExpectedReturn, setDisplayedExpectedReturn] = useState<number | null>(null);
  const [displayedExpectedMultiplier, setDisplayedExpectedMultiplier] = useState<number | null>(null);
  const { generateOgpImage } = useGenerateOgpImage();
  if (!localSelectedChoice) return null;

  const {
    points,
    setPoints,
    isLoadingPotentialFreePoints,
    potentialFreePointsError,
    expectedMultiplier,
    expectedReturn,
    handleSubmit,
    isMutating,
  } = usePredictionMarketEntry({
    predictionMarketId: predictionMarket.id,
    selectedChoice: localSelectedChoice,
    useCompletionModal: true,
  });

  // Use displayedExpectedReturn to avoid showing 0 during loading
  // Only update when we have a valid expectedReturn or when not loading
  useEffect(() => {
    if (!isLoadingPotentialFreePoints && expectedReturn >= 0) {
      setDisplayedExpectedReturn(expectedReturn);
    } else if (displayedExpectedReturn === null && expectedReturn >= 0) {
      // Initialize displayedExpectedReturn on first valid value
      setDisplayedExpectedReturn(expectedReturn);
    }

    if (!isLoadingPotentialFreePoints && expectedMultiplier >= 0) {
      setDisplayedExpectedMultiplier(expectedMultiplier);
    } else if (displayedExpectedMultiplier === null && expectedMultiplier >= 0) {
      // Initialize displayedExpectedMultiplier on first valid value
      setDisplayedExpectedMultiplier(expectedMultiplier);
    }
  }, [
    expectedReturn,
    expectedMultiplier,
    isLoadingPotentialFreePoints,
    displayedExpectedReturn,
    displayedExpectedMultiplier,
  ]);

  const marketType = getPredictionMarketType(predictionMarket.predictionMarketChoices);

  const renderYesNoToggle = () => {
    const { yesChoice, noChoice } = getYesNoChoices(predictionMarket.predictionMarketChoices);

    return (
      <button
        type="button"
        onClick={() => setLocalSelectedChoice(localSelectedChoice.id === yesChoice.id ? noChoice : yesChoice)}
        className="flex items-center"
      >
        {/* <img
          src={
            localSelectedChoice.id === yesChoice.id
              ? "/assets/yes-square.svg"
              : "/assets/no-square.svg"
          }
          alt="Toggle"
          className="w-12 h-12"
        /> */}

        <div
          className={cn(
            "flex justify-center items-center gap-3 px-[17px] py-[12px] rounded-sm text-white text-[18px] font-bold",
            localSelectedChoice.id === yesChoice.id ? "bg-[#00A199]" : "bg-[#FF5A37]",
          )}
        >
          {localSelectedChoice.id === yesChoice.id ? yesChoice.name : noChoice.name}
          <SwitchIcon color="#FFFFFF" />
        </div>
      </button>
    );
  };

  const renderSelectedMultipleChoice = () => {
    const sortedChoices = predictionMarket.predictionMarketChoices.sort((a, b) => b.probability - a.probability);
    const choiceIndex = sortedChoices.findIndex((c) => c.id === selectedChoice.id);
    return (
      <div className={cn("grid gap-1 overflow-y-scroll hide-scrollbar h-[34.5px]")}>
        <ChoiceCard
          className="rounded-[6px]"
          key={sortedChoices[choiceIndex].id}
          id={sortedChoices[choiceIndex].id}
          name={sortedChoices[choiceIndex].name}
          probability={sortedChoices[choiceIndex].probability}
          color={MULTIPLE_COLORS[choiceIndex % MULTIPLE_COLORS.length]}
          backgroundColor={BACKGROUND_MULTIPLE_COLORS[choiceIndex % BACKGROUND_MULTIPLE_COLORS.length]}
          onClick={() => {}}
        />
      </div>
    );
  };

  if (modalStatus === ModalStatus.Finish) {
    return (
      <EntryFinishPopup
        onClickShare={async () => {
          setModalStatus(ModalStatus.Share);
          await generateOgpImage({ marketId: predictionMarket.id });
        }}
        onClose={async () => {
          fetchUser();
          navigate("/prediction-market");
        }}
      />
    );
  }
  const setPointsFn = (updater: (prev: number) => number) => {
    setPoints(updater(points));
  };

  return (
    <>
      {modalStatus === ModalStatus.Share && (
        <Dialog open={true} onOpenChange={() => setModalStatus(ModalStatus.EntryCreation)}>
          <DialogContent className="p-0 [&>button]:hidden gap-0 border-0 overflow-hidden rounded-lg w-[360px]">
            <EntrySharePopup
              market={predictionMarket}
              onClose={() => {
                fetchUser();
                navigate("/prediction-market");
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* {predictionMarket.primaryImageURL && (
        <div className={cn("w-full h-[150px]", isKeyboardOpen && "hidden md:block h-0")}>
          <img
            src={predictionMarket.primaryImageURL}
            alt="round-image"
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>
      )} */}

      <div className="w-full p-4">
        <div className="flex items-center justify-between pb-0 mt-[15px] gap-[10px]">
          {predictionMarket.primaryImageURL && (
            <img
              src={predictionMarket.primaryImageURL}
              alt={predictionMarket.title}
              className="object-cover rounded-lg h-[46px] w-[46px]"
            />
          )}
          <Title
            text={predictionMarket.title}
            className="text-[17px] leading-5 line-clamp-2 font-bold text-ellipsis overflow-hidden"
          />
        </div>
        {marketType === PredictionMarketType.TwoChoice && (
          <div className="mt-4 flex justify-center mb-7">{renderYesNoToggle()}</div>
        )}
        {marketType !== PredictionMarketType.TwoChoice && (
          <div className="mt-5 mb-[30px]">
            {isChangeableMultipleChoice ? (
              <Select
                value={localSelectedChoice?.id}
                onValueChange={(id: string) => {
                  const found = predictionMarket.predictionMarketChoices.find((c) => c.id === id);
                  if (!found) throw new Error("Choice not found");
                  setLocalSelectedChoice(found);
                }}
              >
                <SelectTrigger
                  className={
                    "w-full border-light-gray rounded-lg text-secondary font-semibold ring-0 focus:ring-0 text-center h-10 focus:border-secondary"
                  }
                  iconClassName="opacity-100 text-secondary w-4 h-4 rotate-180"
                  icon={ChevronUp}
                >
                  <p className="w-full text-[16px] font-bold leading-[140%] text-left text-black">
                    {localSelectedChoice.name}
                  </p>
                </SelectTrigger>
                <SelectContent className="rounded-lg h-[300px]">
                  <SelectGroup>
                    {predictionMarket.predictionMarketChoices.map((choice) => (
                      <SelectItem
                        key={choice.id}
                        value={choice.id}
                        className="border-b focus:bg-white focus:text-secondary focus:font-bold rounded-none shadow-none text-base flex-row-reverse justify-between mb-0 pr-0 p-4 mr-0"
                        iconClassName={cn(
                          "opacity-0 text-secondary w-5 h-5 rounded-full bg-secondary p-1",
                          choice.id === localSelectedChoice.id && "opacity-100",
                        )}
                      >
                        {choice.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            ) : (
              renderSelectedMultipleChoice()
            )}
          </div>
        )}

        <div className="flex flex-col items-center gap-10">
          <NumberInputWithControls
            value={points}
            setValue={setPointsFn}
            isShowQuickAdd={isShowQuickAdd}
            throttle={400}
          />

          <div className={cn("my-2 opacity-0 w-full", !potentialFreePointsError && "opacity-100")}>
            <div className="flex items-center justify-center gap-2 w-full">
              <div className="flex justify-end gap-2">
                <div>予想獲得</div>
                <ThumbaCoin className="w-7 h-7" />
              </div>
              <div
                className={cn(
                  "flex items-baseline gap-1 text-primary transition-opacity duration-100 ease-in-out",
                  (displayedExpectedReturn === null || isLoadingPotentialFreePoints) && "opacity-0",
                )}
              >
                <div className="text-[28px] leading-[28px] font-extrabold">
                  {displayedExpectedReturn !== null ? displayedExpectedReturn.toLocaleString() : 0}
                </div>
                <div className="text-2xl font-medium">pt</div>
              </div>
            </div>
            {/* <div className="text-sm w-full text-center">
              期待値:{" "}
              <span
                className={cn(
                  "font-bold transition-opacity duration-100 ease-in-out",
                  (displayedExpectedMultiplier === null ||
                    isLoadingPotentialFreePoints) &&
                    "opacity-0"
                )}
              >
                ×
                {displayedExpectedMultiplier !== null
                  ? displayedExpectedMultiplier
                  : 0}
              </span>
            </div> */}
          </div>

          {/* Submit Button */}
          <div className="w-full">
            <PrimaryButton
              content="予想してポイントゲット！"
              onClick={async () => {
                if (isMutating) return; // Prevent multiple submissions

                if (points > userPoints || userPoints === 0) {
                  setOpenInsufficientModal(true);
                  return;
                }
                if (isNeedConfirmation) {
                  handleConfirmEntry?.(points, localSelectedChoice as PredictionMarketChoiceWithProbability);
                  return;
                }
                await handleSubmit();

                setModalStatus(ModalStatus.Finish);
              }}
              disabled={isLoadingPotentialFreePoints}
              className="mt-2 bg-gradient-primary mb-3"
            />
            <div className="text-xs text-gray text-left">
              <span>現在の所持ポイント：</span>
              <span className="font-bold">{userPoints.toLocaleString()} pt</span>
            </div>
          </div>

          <InsufficientModal
            open={openInsufficientModal}
            setOpen={setOpenInsufficientModal}
            entryFee={points}
            userPoints={userPoints}
          />
        </div>
      </div>
    </>
  );
};
